import { useState, useRef, useEffect } from 'react';
import { useMutation } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { isAdmin, UserRole, getRoleLabel, STAFF_ROLES } from '../../utils/permissions';

const DISABLE_USER = gql`
  mutation DisableUser($userId: ID!) {
    disableUser(userId: $userId) {
      id
      isEnabled
      loginDisabled
    }
  }
`;

const ENABLE_USER = gql`
  mutation EnableUser($userId: ID!) {
    enableUser(userId: $userId) {
      id
      isEnabled
      loginDisabled
    }
  }
`;

const DISABLE_USER_LOGIN = gql`
  mutation DisableUserLogin($userId: ID!) {
    disableUserLogin(userId: $userId) {
      id
      isEnabled
      loginDisabled
    }
  }
`;

const ENABLE_USER_LOGIN = gql`
  mutation EnableUserLogin($userId: ID!) {
    enableUserLogin(userId: $userId) {
      id
      isEnabled
      loginDisabled
    }
  }
`;

const RESET_USER_PASSWORD = gql`
  mutation ResetUserPassword($userId: ID!) {
    resetUserPassword(userId: $userId) {
      success
      message
      temporaryPassword
    }
  }
`;

const SEND_PASSWORD_RESET_EMAIL = gql`
  mutation SendPasswordResetEmail($userId: ID!, $temporaryPassword: String!) {
    sendPasswordResetEmail(userId: $userId, temporaryPassword: $temporaryPassword) {
      success
      message
    }
  }
`;

const UPDATE_USER = gql`
  mutation UpdateUser($id: ID!, $input: UpdateUserInput!) {
    updateUser(id: $id, input: $input) {
      id
      role
    }
  }
`;

interface ResetPasswordResult {
  success: boolean;
  message: string;
  temporaryPassword: string;
}

interface SendPasswordResetEmailResult {
  success: boolean;
  message: string;
}

interface UserManagementActionsProps {
  user: any;
  currentUserRole: string;
  onActionComplete: () => void;
}

export function UserManagementActions({ user, currentUserRole, onActionComplete }: UserManagementActionsProps) {
  const navigate = useNavigate();
  const [showDropdown, setShowDropdown] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showRoleChangeModal, setShowRoleChangeModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState(user.role);
  const [temporaryPassword, setTemporaryPassword] = useState('');
  const [menuPosition, setMenuPosition] = useState<'bottom' | 'top'>('bottom');
  const [emailSent, setEmailSent] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [changingRole, setChangingRole] = useState(false);
  const buttonRef = useRef<HTMLDivElement>(null);

  const [disableUser] = useMutation(DISABLE_USER);
  const [enableUser] = useMutation(ENABLE_USER);
  const [disableUserLogin] = useMutation(DISABLE_USER_LOGIN);
  const [enableUserLogin] = useMutation(ENABLE_USER_LOGIN);
  const [resetPassword] = useMutation<{ resetUserPassword: ResetPasswordResult }>(RESET_USER_PASSWORD);
  const [sendPasswordResetEmail] = useMutation<{ sendPasswordResetEmail: SendPasswordResetEmailResult }>(SEND_PASSWORD_RESET_EMAIL);
  const [updateUser] = useMutation(UPDATE_USER);

  const userIsAdmin = isAdmin(currentUserRole as UserRole);
  
  // Check if user is CLIENT or PROSPECT (cannot be promoted to staff roles)
  const isClientOrProspect = user.role === UserRole.CLIENT || user.role === UserRole.PROSPECT;

  // Get available roles for the dropdown based on current user's role
  const getAvailableRoles = () => {
    if (isClientOrProspect) {
      // CLIENT/PROSPECT can only switch between CLIENT and PROSPECT
      return [
        { value: UserRole.CLIENT, label: getRoleLabel(UserRole.CLIENT) },
        { value: UserRole.PROSPECT, label: getRoleLabel(UserRole.PROSPECT) },
      ];
    }
    // Staff users can be changed to any role
    return [
      { value: UserRole.ADMIN, label: getRoleLabel(UserRole.ADMIN) },
      { value: UserRole.ATTORNEY, label: getRoleLabel(UserRole.ATTORNEY) },
      { value: UserRole.CASE_MANAGER, label: getRoleLabel(UserRole.CASE_MANAGER) },
      { value: UserRole.OFFICE_MANAGER, label: getRoleLabel(UserRole.OFFICE_MANAGER) },
      { value: UserRole.INQUIRY_MANAGER, label: getRoleLabel(UserRole.INQUIRY_MANAGER) },
      { value: UserRole.LEAD_CASE_MANAGER, label: getRoleLabel(UserRole.LEAD_CASE_MANAGER) },
      { value: UserRole.MEDICAL_RECORDS_COORDINATOR, label: getRoleLabel(UserRole.MEDICAL_RECORDS_COORDINATOR) },
      { value: UserRole.PARALEGAL, label: getRoleLabel(UserRole.PARALEGAL) },
      { value: UserRole.CLIENT, label: getRoleLabel(UserRole.CLIENT) },
      { value: UserRole.PROSPECT, label: getRoleLabel(UserRole.PROSPECT) },
    ];
  };

  useEffect(() => {
    if (showDropdown && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      
      // If less than 250px space below and more space above, open upward
      if (spaceBelow < 250 && spaceAbove > spaceBelow) {
        setMenuPosition('top');
      } else {
        setMenuPosition('bottom');
      }
    }
  }, [showDropdown]);

  const handleView = () => {
    if (user.role === 'CLIENT') {
      navigate(`/clients/${user.id}`);
    } else {
      // For staff, we could navigate to a user detail page or just show info
      alert('Staff user detail view - to be implemented');
    }
    setShowDropdown(false);
  };

  const handleEdit = () => {
    if (user.role === 'CLIENT') {
      navigate(`/clients/${user.id}/edit`);
    } else {
      navigate(`/admin/users/${user.id}/edit`);
    }
    setShowDropdown(false);
  };

  const handleSendEmail = () => {
    window.location.href = `mailto:${user.email}`;
    setShowDropdown(false);
  };

  const handleDisable = async () => {
    if (!confirm(`Are you sure you want to restrict access for ${user.firstName} ${user.lastName}? They will see a read-only screen but can still log in.`)) {
      return;
    }

    try {
      await disableUser({ variables: { userId: user.id } });
      alert('User access restricted successfully');
      onActionComplete();
    } catch (error: any) {
      console.error('Error disabling user:', error);
      alert(`Error: ${error.message}`);
    }
    setShowDropdown(false);
  };

  const handleEnable = async () => {
    try {
      await enableUser({ variables: { userId: user.id } });
      alert('User access restored successfully');
      onActionComplete();
    } catch (error: any) {
      console.error('Error enabling user:', error);
      alert(`Error: ${error.message}`);
    }
    setShowDropdown(false);
  };

  const handleBlockLogin = async () => {
    if (!confirm(`Are you sure you want to block login for ${user.firstName} ${user.lastName}? They will NOT be able to sign in at all.`)) {
      return;
    }

    try {
      await disableUserLogin({ variables: { userId: user.id } });
      alert('User login blocked successfully');
      onActionComplete();
    } catch (error: any) {
      console.error('Error blocking user login:', error);
      alert(`Error: ${error.message}`);
    }
    setShowDropdown(false);
  };

  const handleEnableLogin = async () => {
    try {
      await enableUserLogin({ variables: { userId: user.id } });
      alert('User login enabled successfully');
      onActionComplete();
    } catch (error: any) {
      console.error('Error enabling user login:', error);
      alert(`Error: ${error.message}`);
    }
    setShowDropdown(false);
  };

  const handleResetPassword = async () => {
    if (!confirm(`Reset password for ${user.firstName} ${user.lastName}? A temporary password will be generated.`)) {
      return;
    }

    try {
      const result = await resetPassword({ variables: { userId: user.id } });
      if (result.data?.resetUserPassword) {
        setTemporaryPassword(result.data.resetUserPassword.temporaryPassword);
        setShowPasswordModal(true);
      }
    } catch (error: any) {
      console.error('Error resetting password:', error);
      alert(`Error: ${error.message}`);
    }
    setShowDropdown(false);
  };

  const handleOpenRoleChange = () => {
    setSelectedRole(user.role);
    setShowRoleChangeModal(true);
    setShowDropdown(false);
  };

  const handleConfirmRoleChange = async () => {
    if (selectedRole === user.role) {
      setShowRoleChangeModal(false);
      return;
    }

    setChangingRole(true);
    try {
      await updateUser({
        variables: {
          id: user.id,
          input: { role: selectedRole }
        }
      });
      alert(`Role changed successfully to ${getRoleLabel(selectedRole)}`);
      setShowRoleChangeModal(false);
      onActionComplete();
    } catch (error: any) {
      console.error('Error changing role:', error);
      alert(`Error changing role: ${error.message}`);
    } finally {
      setChangingRole(false);
    }
  };

  const handleCopyPassword = () => {
    navigator.clipboard.writeText(temporaryPassword);
    alert('Password copied to clipboard!');
  };

  const handleSendPasswordEmail = async () => {
    setSendingEmail(true);
    try {
      const result = await sendPasswordResetEmail({
        variables: { userId: user.id, temporaryPassword }
      });
      if (result.data?.sendPasswordResetEmail?.success) {
        setEmailSent(true);
      } else {
        alert('Failed to send email: ' + (result.data?.sendPasswordResetEmail?.message || 'Unknown error'));
      }
    } catch (error: any) {
      console.error('Error sending password reset email:', error);
      alert(`Error sending email: ${error.message}`);
    } finally {
      setSendingEmail(false);
    }
  };

  const getEmailTemplate = () => {
    return `Hi ${user.firstName},

Your password has been reset. Here is your temporary password:

${temporaryPassword}

You will be required to change this password when you log in.

Best regards,
AA Law Team`;
  };

  return (
    <>
      <div className="relative inline-block text-left" ref={buttonRef}>
        <button
          onClick={() => setShowDropdown(!showDropdown)}
          className="inline-flex items-center px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-aalaw-red"
        >
          Actions
          <svg className="ml-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {showDropdown && (
          <>
            <div
              className="fixed inset-0 z-10"
              onClick={() => setShowDropdown(false)}
            />
            <div className={`origin-top-right absolute right-0 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-20 ${
              menuPosition === 'top' ? 'bottom-full mb-2' : 'mt-2'
            }`}>
              <div className="py-1">
                <button
                  onClick={handleView}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  View Details
                </button>

                <button
                  onClick={handleEdit}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Edit User
                </button>

                {userIsAdmin && (
                  <button
                    onClick={handleOpenRoleChange}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    Change Role
                  </button>
                )}

                <button
                  onClick={handleSendEmail}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  Send Email
                </button>

                {userIsAdmin && (
                  <>
                    <div className="border-t border-gray-100 my-1"></div>
                    
                    {/* Screen Access Control */}
                    <div className="px-4 py-1 text-xs font-semibold text-gray-400 uppercase">Screen Access</div>
                    {user.isEnabled !== false ? (
                      <button
                        onClick={handleDisable}
                        className="w-full text-left px-4 py-2 text-sm text-yellow-600 hover:bg-yellow-50 flex items-center"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                        Restrict Access
                      </button>
                    ) : (
                      <button
                        onClick={handleEnable}
                        className="w-full text-left px-4 py-2 text-sm text-green-600 hover:bg-green-50 flex items-center"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        Restore Access
                      </button>
                    )}

                    {/* Login Access Control */}
                    <div className="px-4 py-1 text-xs font-semibold text-gray-400 uppercase mt-1">Login Access</div>
                    {!user.loginDisabled ? (
                      <button
                        onClick={handleBlockLogin}
                        className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                        </svg>
                        Block Login
                      </button>
                    ) : (
                      <button
                        onClick={handleEnableLogin}
                        className="w-full text-left px-4 py-2 text-sm text-green-600 hover:bg-green-50 flex items-center"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Enable Login
                      </button>
                    )}

                    <div className="border-t border-gray-100 my-1"></div>

                    <button
                      onClick={handleResetPassword}
                      className="w-full text-left px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 flex items-center"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                      </svg>
                      Reset Password
                    </button>
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Temporary Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            {/* Header */}
            <div className="bg-gradient-to-r from-green-500 to-green-600 px-6 py-4 rounded-t-lg">
              <div className="flex items-center gap-3">
                <div className="bg-white rounded-full p-2">
                  <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-white">
                  Password Reset Successful
                </h3>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-5">
              {/* Temporary Password Section */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Temporary Password
                </label>
                <div className="flex flex-col sm:flex-row gap-2">
                  <div className="flex-1 bg-gray-50 border-2 border-gray-300 rounded-lg px-4 py-3 min-w-0">
                    <code className="text-lg font-mono font-bold text-gray-900 break-all">
                      {temporaryPassword}
                    </code>
                  </div>
                  <button
                    onClick={handleCopyPassword}
                    className="sm:w-auto px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors flex items-center justify-center gap-2 shadow-sm flex-shrink-0"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    Copy
                  </button>
                </div>
              </div>

              {/* Important Notice */}
              <div className="bg-amber-50 border-l-4 border-amber-500 rounded-r-lg p-4">
                <div className="flex gap-3">
                  <svg className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-amber-900 mb-1">Important</p>
                    <p className="text-sm text-amber-800 leading-relaxed break-words">
                      The user must change this password on their next login. This temporary password will expire after first use.
                    </p>
                  </div>
                </div>
              </div>

              {/* Send Email Section */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div className="flex items-center gap-3 min-w-0">
                    <svg className="w-6 h-6 text-blue-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <div className="min-w-0">
                      <p className="font-semibold text-blue-900">Send Password via Email</p>
                      <p className="text-sm text-blue-700 truncate">To: {user.email}</p>
                    </div>
                  </div>
                  {emailSent ? (
                    <div className="flex items-center gap-2 text-green-600 font-semibold flex-shrink-0">
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Email Sent!
                    </div>
                  ) : (
                    <button
                      onClick={handleSendPasswordEmail}
                      disabled={sendingEmail}
                      className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg font-semibold transition-colors flex items-center gap-2 shadow-sm flex-shrink-0"
                    >
                      {sendingEmail ? (
                        <>
                          <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Sending...
                        </>
                      ) : (
                        <>
                          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                          </svg>
                          Send Email
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>

              {/* Email Template Section (Collapsible) */}
              <details className="group">
                <summary className="flex items-center gap-2 cursor-pointer text-sm font-semibold text-gray-600 hover:text-gray-800">
                  <svg className="w-4 h-4 transition-transform group-open:rotate-90" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                  View Email Template (for manual copy)
                </summary>
                <div className="mt-3 bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <textarea
                    readOnly
                    value={getEmailTemplate()}
                    className="w-full text-sm font-mono bg-white border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                    rows={8}
                    onClick={(e) => e.currentTarget.select()}
                  />
                  <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Click the text to select all, then copy and paste into your email
                  </p>
                </div>
              </details>

              {/* Close Button */}
              <button
                onClick={() => {
                  setShowPasswordModal(false);
                  setEmailSent(false);
                }}
                className="w-full px-6 py-3 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white rounded-lg font-semibold transition-all shadow-md hover:shadow-lg"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Role Change Modal */}
      {showRoleChangeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Change Role for {user.firstName} {user.lastName}
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Current Role: <span className="font-semibold">{getRoleLabel(user.role)}</span>
                </label>
                <select
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-aalaw-red"
                >
                  {getAvailableRoles().map((role) => (
                    <option key={role.value} value={role.value}>
                      {role.label}
                    </option>
                  ))}
                </select>
              </div>

              {isClientOrProspect && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-yellow-800 text-sm">
                    ⚠️ Client and Prospect users cannot be promoted to staff roles.
                  </p>
                </div>
              )}

              {selectedRole !== user.role && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-red-800 text-sm">
                    <strong>Warning:</strong> Changing the role will:
                  </p>
                  <ul className="text-sm text-red-700 list-disc list-inside mt-2 space-y-1">
                    <li>Update the role in both Cognito and the database</li>
                    <li>Change the user's permissions immediately</li>
                    <li>May affect the user's access to certain features</li>
                  </ul>
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setShowRoleChangeModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded hover:bg-gray-50 font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmRoleChange}
                  disabled={changingRole || selectedRole === user.role}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {changingRole ? 'Changing...' : 'Confirm Change'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
